// Purchasing API
import api, { type AxiosResponse } from './client';
import type {
  PurchaseOrder,
  PurchaseOrderLine,
  PODocument,
  GRNDocument,
  ASNDocument,
  PurchasingDocumentDownload,
  ReasonCode,
  ReasonCodeAudit,
  POLineSchedule,
  POChangeLog,
  POChangeRequestAttachment,
  GoodsReceiptNote,
  AdvancedShippingNotice,
  ASNColumnFormatResponse,
  WriteBack,
  WriteBackStats,
  SupplierQuery,
  SupplierQueryType,
  SupplierQueryPriority,
  SupplierQueryStats,
  ForecastTableResponse,
  ForecastCellDetail,
  ForecastFilterOptions,
  POForecast,
  PartSchedule,
  PartScheduleEntry,
  PartNotFromErp,
  ForecastHistory,
  PurchasingDashboardStats,
  ContractPrice,
  ContractPricePart,
  POLinePricebookResponse,
  ContractPriceChangelogResponse,
  ContractPriceImportPreview,
  ContractType,
  ContractFormOptions,
  QueryParams,
  POLineMessage,
  ASNMessage,
  CommodityGroup,
  CommodityType,
  AckUpload,
  POLineGRNSummary,
  POInsights,
} from './types';

/** A schedule-order PO row in the OTIF-exclusions management list. */
export interface OTIFExclusionRow {
  id: number;
  po_number: string;
  supplier_name: string | null;
  order_type: string;
  erp_order_type: string;
  exclude_from_otif: boolean;
  schedule_count: number;
  part_numbers: string | null;
}

/** Result of a supplier bulk delivery-appeal submission. */
export interface BulkAppealResult {
  status: string;
  created_count: number;
  skipped_count: number;
  created: { id: number; grn_id: number; grn_number: string }[];
  skipped: { grn_id: number; grn_number?: string; reason: string }[];
}

/** CR-8214: result of a customer bulk GRN acknowledgement. */
export interface BulkAcknowledgeResult {
  status: string;
  acknowledged_count: number;
  skipped_count: number;
  acknowledged: { grn_id: number; grn_number: string }[];
  skipped: { grn_id: number; grn_number?: string; reason: string }[];
}

export interface BulkRespondResult {
  status: string;
  action: 'accept' | 'reject';
  processed_count: number;
  skipped_count: number;
  processed: { id: number; grn_id: number }[];
  skipped: { appeal_id: number; reason: string }[];
}

/** Bucket keys returned by /purchasing/lines/expediting-summary/. */
// W3-T20 FIX-10: 'expedited' was a fourth value of a PROXIMITY bucket and
// unreachable for the lines it was for; the flag is now its own axis.
export type ExpeditingBucket = 'on_time' | 'at_risk' | 'late';

export interface POLineExpeditingSummary {
  /** ISO date the buckets were computed against. */
  as_of: string;
  at_risk_window_days: number;
  total: number;
  /** Count + value (reporting currency, as a decimal string) per bucket. */
  buckets: Record<ExpeditingBucket, { count: number; value: string }>;
  /** Top suppliers by (late + at risk), each with a count per bucket. */
  // FIX-10: the flag is a SECOND axis, not a bucket — a flagged line is
  // counted in its proximity bucket AND here.
  flagged: { count: number; value: string };
  by_supplier: Array<{ supplier: string; flagged: number } & Record<ExpeditingBucket, number>>;
}

export const purchasingAPI = {
  // Purchase Orders
  getOrders: (params?: QueryParams): Promise<AxiosResponse<{ count: number; results: PurchaseOrder[] }>> =>
    api.get('/purchasing/orders/', { params }),

  // Export ALL filtered PO Headers (not just current page). Defaults to xlsx.
  exportOrders: (params?: QueryParams): Promise<AxiosResponse<Blob>> =>
    api.get('/purchasing/orders/export/', { params, responseType: 'blob' }),

  getOrder: (id: number): Promise<AxiosResponse<PurchaseOrder>> =>
    api.get(`/purchasing/orders/${id}/`),

  /** Customer review 11 Sep (items 25/26): OTIF %, PPM/K, buyers and per-line NCR / ASN for one order. */
  getOrderInsights: (id: number): Promise<AxiosResponse<POInsights>> =>
    api.get(`/purchasing/orders/${id}/insights/`),

  getDashboardStats: (params?: QueryParams): Promise<AxiosResponse<PurchasingDashboardStats>> =>
    api.get('/purchasing/orders/dashboard_stats/', { params }),

  // Lazy-load modal data for dashboard charts
  getDashboardModalData: (modalType: string, params?: QueryParams): Promise<AxiosResponse<Record<string, unknown[]>>> =>
    api.get('/purchasing/orders/dashboard_modal_data/', { params: { modal_type: modalType, ...params } }),

  acknowledgeOrder: (id: number): Promise<AxiosResponse<PurchaseOrder>> =>
    api.post(`/purchasing/orders/${id}/acknowledge/`),

  updateStatus: (id: number, status: string, reason?: string): Promise<AxiosResponse<PurchaseOrder>> =>
    api.post(`/purchasing/orders/${id}/update_status/`, { status, reason }),

  // PO Lines
  getLines: (params?: QueryParams): Promise<AxiosResponse<{ count: number; results: PurchaseOrderLine[] }>> =>
    api.get('/purchasing/lines/', { params }),

  getLine: (id: number): Promise<AxiosResponse<PurchaseOrderLine>> =>
    api.get(`/purchasing/lines/${id}/`),

  /**
   * Server-side expediting buckets for the dashboard Expediting tab — one
   * GROUP BY instead of paging the whole line list into the browser. Accepts
   * the same filters as getLines (status__in, supplier, view context…).
   */
  getExpeditingSummary: (params?: QueryParams): Promise<AxiosResponse<POLineExpeditingSummary>> =>
    api.get('/purchasing/lines/expediting-summary/', { params }),

  // CR-1558: Export PO Lines with filters
  exportLines: (params?: QueryParams): Promise<AxiosResponse<Blob>> =>
    api.get('/purchasing/lines/export/', { params, responseType: 'blob' }),

  // CR-3413: Get filter options for PO Lines
  getLineFilterOptions: (params?: QueryParams): Promise<AxiosResponse<{
    suppliers: Array<{
      id: number;
      name: string;
      group?: number | null;
      country?: string | null;  // CR-6439: Supplier country for filtering
      customers?: number[];  // CR-6439: Site IDs this supplier serves
    }>;
    customers: Array<{ id: number; name: string; group?: number | null; country?: string | null }>;
    commodity_groups: Array<{ id: number; name: string }>;
    commodity_types: Array<{ id: number; name: string; commodity_group_id: number }>;
    // CR-6348: option-lists for the reworked SO Lines filter panel.
    supplier_groups?: Array<{ id: number; name: string }>;
    divisions?: Array<{ id: number; name: string }>;
    warehouses?: Array<{ id: number; name: string; site: number; code?: string | null }>;  // CR-6449: code for the ASN warehouse filter
    site_countries?: string[];
    supplier_countries?: string[];
    /** CR-6347 / CR-6439: raw ERP order-type codes present in scope, for the Order Type filter. */
    erp_order_types?: string[];
    // CR-7400: the ERP buyer codes on the PO lines in scope, for the Buyer ID
    // multi-select. Scoped like the list, so the caller passes the same view
    // params (site / group / company) it sends the list.
    buyer_ids?: string[];
  }>> =>
    api.get('/purchasing/lines/filter-options/', { params }),

  // PO Line Acknowledgment Workflow Actions
  // timeout: 120s — these trigger async Celery tasks on the backend which may take a moment
  acceptDeliveryDate: (lineId: number, data: { comments?: string }): Promise<AxiosResponse<{ status: string; message: string; data: PurchaseOrderLine }>> =>
    api.post(`/purchasing/lines/${lineId}/accept-delivery-date/`, data, { timeout: 120000 }),

  // CR-6190: override a delivered-but-failed line's OTIF to pass (admin only, comment required).
  overrideOtif: (lineId: number, data: { comments: string }): Promise<AxiosResponse<{ status: string; message: string; data: PurchaseOrderLine }>> =>
    api.post(`/purchasing/lines/${lineId}/override-otif/`, data, { timeout: 120000 }),

  // CR-6191: OTIF Review workflow — acknowledge a fail, reset a decision, and fire a test email.
  acknowledgeOtif: (lineId: number, data: { comments: string }): Promise<AxiosResponse<{ status: string; message: string; data: PurchaseOrderLine }>> =>
    api.post(`/purchasing/lines/${lineId}/acknowledge-otif/`, data, { timeout: 120000 }),

  resetOtif: (lineId: number, data: { comments: string }): Promise<AxiosResponse<{ status: string; message: string; data: PurchaseOrderLine }>> =>
    api.post(`/purchasing/lines/${lineId}/reset-otif/`, data, { timeout: 120000 }),

  sendOtifReviewTest: (): Promise<AxiosResponse<{ status: string; message: string; data: Record<string, number> }>> =>
    api.post('/purchasing/lines/otif-review/send-test/', {}),

  acceptQuantityChange: (lineId: number, data: { comments?: string }): Promise<AxiosResponse<{ status: string; message: string; data: PurchaseOrderLine }>> =>
    api.post(`/purchasing/lines/${lineId}/accept-quantity-change/`, data, { timeout: 120000 }),

  acceptPriceChange: (lineId: number, data: { comments?: string }): Promise<AxiosResponse<{ status: string; message: string; data: PurchaseOrderLine }>> =>
    api.post(`/purchasing/lines/${lineId}/accept-price-change/`, data, { timeout: 120000 }),

  declineDeliveryDate: (lineId: number, data: { reason: string; comments?: string; bulk?: boolean }): Promise<AxiosResponse<{ status: string; message: string; data: PurchaseOrderLine }>> =>
    api.post(`/purchasing/lines/${lineId}/decline-delivery-date/`, data, { timeout: 120000 }),

  // CR-4688: Per-field reject for partial acceptance/rejection of multi-field change requests
  // `counter` (EXPEDITING-PARITY PR 2, G8): reject AND propose your own value
  // in one action — the line then waits on the other side instead of stopping
  // at Change Declined. Its type follows `field`: a date, a qty or a price.
  // EXPEDITING-PARITY PR 2 (G12): a mixed proposal is ONE request. The modal
  // used to loop one call per field, so a later leg that 400d left the earlier
  // ones committed and the line carried half a proposal nobody sent.
  proposeChanges: (lineId: number, data: { changes: Array<{ field: 'date' | 'quantity' | 'price'; value: string; reason: string }>; comments?: string }): Promise<AxiosResponse<{ status: string; message: string; data: PurchaseOrderLine }>> =>
    api.post(`/purchasing/lines/${lineId}/propose-changes/`, data, { timeout: 120000 }),

  rejectFieldChange: (lineId: number, data: { field: string; reason: string; comments?: string; counter?: string | number }): Promise<AxiosResponse<{ status: string; message: string; data: PurchaseOrderLine }>> =>
    api.post(`/purchasing/lines/${lineId}/reject-field-change/`, data, { timeout: 120000 }),

  // CR-4680: Supplier cancels their own proposed field change
  cancelFieldChange: (lineId: number, data: { field: string; reason?: string; comments?: string }): Promise<AxiosResponse<{ status: string; message: string; data: PurchaseOrderLine }>> =>
    api.post(`/purchasing/lines/${lineId}/cancel-field-change/`, data, { timeout: 120000 }),

  /** EXPEDITING-3: the side whose request was declined closes it -- back to WIP, nothing moves. */
  closeDeclinedRequest: (lineId: number, data: { comments?: string } = {}): Promise<AxiosResponse<{ status: string; message: string; data: PurchaseOrderLine }>> =>
    api.post(`/purchasing/lines/${lineId}/close-declined-request/`, data),

  proposeAlternativeDate: (lineId: number, data: { proposedDate: string; reason: string; comments?: string }): Promise<AxiosResponse<{ status: string; message: string; data: PurchaseOrderLine }>> =>
    api.post(`/purchasing/lines/${lineId}/propose-alternative-date/`, data),

  /**
   * EXPEDITING-PARITY PR 2 (P10): the customer sets the three dates OUTRIGHT
   * while the line is still `new` — not a proposal. The door shuts the moment
   * the supplier acknowledges; the server enforces that, this is only the call.
   *
   * Keys are the API's pinned spellings: `customerRequiredDate` is the column
   * the payload calls `delivery_date`, `originalRequiredDate` the one it calls
   * `original_promise_date`. Omit a date to leave it alone.
   */
  setLineDates: (lineId: number, data: { customerRequiredDate?: string; originalRequiredDate?: string; supplierPromiseDate?: string; reason?: string; comments?: string }): Promise<AxiosResponse<{ status: string; message: string; data: PurchaseOrderLine }>> =>
    api.post(`/purchasing/lines/${lineId}/set-dates/`, data),

  proposeQuantityChange: (lineId: number, data: { proposedQuantity: string; reason: string; comments?: string }): Promise<AxiosResponse<{ status: string; message: string; data: PurchaseOrderLine }>> =>
    api.post(`/purchasing/lines/${lineId}/propose-quantity-change/`, data),

  proposePriceChange: (lineId: number, data: { proposedPrice: string; reason: string; comments?: string }): Promise<AxiosResponse<{ status: string; message: string; data: PurchaseOrderLine }>> =>
    api.post(`/purchasing/lines/${lineId}/propose-price-change/`, data),

  // PO Line Hold Management
  // nextActionWith (EXPEDITING-PARITY PR 2, G7): whose move it is while the
  // line is held, and therefore the only side offered the Release control.
  // Omitted = next action with me, which is how every hold behaved before the
  // dialog asked.
  putLineOnHold: (lineId: number, data: { reason: string; nextActionWith?: 'customer' | 'supplier' }): Promise<AxiosResponse<{ status: string; message: string; data: PurchaseOrderLine }>> =>
    api.post(`/purchasing/lines/${lineId}/put-on-hold/`, data),

  releaseLineHold: (lineId: number): Promise<AxiosResponse<{ status: string; message: string; data: PurchaseOrderLine }>> =>
    api.post(`/purchasing/lines/${lineId}/release-hold/`),

  // PO Line Critical Flag Management
  setLineCritical: (lineId: number, is_critical: boolean): Promise<AxiosResponse<{ status: string; message: string; data: PurchaseOrderLine }>> =>
    api.post(`/purchasing/lines/${lineId}/set-critical/`, { is_critical }),

  // PO Line Enhancements - GRN, Shortage, and Batch Tracking
  // Customer review 11 Sep (item 30c): totals and rows fold in NCR rejections.
  getLineGRNSummary: (lineId: number): Promise<AxiosResponse<POLineGRNSummary>> =>
    api.get(`/purchasing/lines/${lineId}/grn_summary/`),

  /** CR-6411: contract price bands for this line's part, grouped by contract.
   *  Backs the Pricebooks tab on the PO Line pop-up. Customer-side only —
   *  the server returns 403 for supplier accounts (CR-5552). */
  getLinePricebookParts: (lineId: number): Promise<AxiosResponse<POLinePricebookResponse>> =>
    api.get(`/purchasing/lines/${lineId}/pricebook-parts/`),

  recordLineShortage: (lineId: number, data: {
    shortage_qty: string | number;
    reason_id: number;
    notes?: string;
  }): Promise<AxiosResponse<{ status: string; message: string; data: PurchaseOrderLine }>> =>
    api.post(`/purchasing/lines/${lineId}/record_shortage/`, data),

  resolveLineShortage: (lineId: number, data: {
    resolution_notes?: string;
  }): Promise<AxiosResponse<{ status: string; message: string; data: PurchaseOrderLine }>> =>
    api.post(`/purchasing/lines/${lineId}/resolve_shortage/`, data),

  getLineBatchHistory: (lineId: number): Promise<AxiosResponse<{
    part_number: string;
    line_batch_number: string;
    packing_note_number: string;
    total_batches: number;
    grn_batches: Array<{
      id: number;
      grn_number: string;
      batch_number: string;
      receipt_date: string;
      quantity_received: string;
      quantity_accepted: string;
      quantity_rejected: string;
      inspection_status: string;
      packing_slip_number: string;
    }>;
  }>> =>
    api.get(`/purchasing/lines/${lineId}/batch_history/`),

  // OTIF Exclusions — schedule-order POs left out of OTIF calculations
  // (still shown in schedule orders / forecast / deliveries).
  getOTIFExclusions: (params?: QueryParams): Promise<AxiosResponse<{ count: number; results: OTIFExclusionRow[] }>> =>
    api.get('/purchasing/otif-exclusions/', { params }),

  setOTIFExclusion: (id: number, excludeFromOtif: boolean): Promise<AxiosResponse<OTIFExclusionRow>> =>
    api.patch(`/purchasing/otif-exclusions/${id}/`, { exclude_from_otif: excludeFromOtif }),

  // PO Line Schedules (ASN)
  /** All schedule quick-filter tab counts in one request (CR-6543 r6) —
   *  replaces seven parallel page_size=1 count probes. */
  getScheduleTabCounts: (tabs: Record<string, Record<string, string | number | boolean>>): Promise<AxiosResponse<{ counts: Record<string, number | null> }>> =>
    api.post('/purchasing/schedules/tab-counts/', { tabs }),

  /** Batched PO-line quick-filter counts — one request instead of one per tab. */
  getPOLineTabCounts: (tabs: Record<string, Record<string, string | number | boolean>>): Promise<AxiosResponse<{ counts: Record<string, number | null> }>> =>
    api.post('/purchasing/lines/tab-counts/', { tabs }),

  getSchedules: (params?: QueryParams): Promise<AxiosResponse<{ count: number; results: POLineSchedule[] }>> =>
    api.get('/purchasing/schedules/', { params }),

  getSchedule: (id: number, params?: QueryParams): Promise<AxiosResponse<POLineSchedule>> =>
    api.get(`/purchasing/schedules/${id}/`, { params }),

  // Schedule collaboration actions
  acceptScheduleDate: (id: number, data: { comments?: string }): Promise<AxiosResponse<{ status: string; message: string; data: POLineSchedule }>> =>
    api.post(`/purchasing/schedules/${id}/accept-delivery-date/`, data),
  declineScheduleDate: (id: number, data: { reason: string; comments?: string }): Promise<AxiosResponse<{ status: string; message: string; data: POLineSchedule }>> =>
    api.post(`/purchasing/schedules/${id}/decline-delivery-date/`, data),
  proposeScheduleDate: (id: number, data: { proposed_date: string; reason: string; comments?: string }): Promise<AxiosResponse<{ status: string; message: string; data: POLineSchedule }>> =>
    api.post(`/purchasing/schedules/${id}/propose-alternative-date/`, data),
  proposeScheduleQty: (id: number, data: { proposed_quantity: number; reason: string; comments?: string }): Promise<AxiosResponse<{ status: string; message: string; data: POLineSchedule }>> =>
    api.post(`/purchasing/schedules/${id}/propose-quantity-change/`, data),
  putScheduleOnHold: (id: number, data: { reason: string }): Promise<AxiosResponse<{ status: string; message: string; data: POLineSchedule }>> =>
    api.post(`/purchasing/schedules/${id}/put-on-hold/`, data),
  releaseScheduleHold: (id: number): Promise<AxiosResponse<{ status: string; message: string; data: POLineSchedule }>> =>
    api.post(`/purchasing/schedules/${id}/release-hold/`),
  // Manual OTIF override (staff/superuser only). {reason} sets the override
  // (marks the schedule OTIF-passed), {clear: true} removes an existing one.
  overrideScheduleOtif: (id: number, data: { reason: string } | { clear: true }): Promise<AxiosResponse<{ detail: string; otif_overridden: boolean; otif_override_reason?: string; otif_overridden_by_name?: string; otif_overridden_at?: string }>> =>
    api.post(`/purchasing/schedules/${id}/otif-override/`, data),
  // Close the remainder of a manually agreed (collab-held) schedule line —
  // customer side only, reason mandatory. Receipts stay on the line; the
  // open balance is zeroed and the hold released.
  closeHeldSchedule: (id: number, data: { reason: string }): Promise<AxiosResponse<{ detail: string; schedule_id: number; status: string; acknowledge_status: string; balance_qty: string; collab_hold: boolean }>> =>
    api.post(`/purchasing/schedules/${id}/close-held/`, data),

  // PHASE 1 - Reason Codes (CR-5886: optional description, archive + audit)
  getReasonCodes: (params?: QueryParams): Promise<AxiosResponse<{ count: number; results: ReasonCode[] }>> =>
    api.get('/purchasing/reason-codes/', { params }),
  createReasonCode: (data: { code: string; description?: string; category: string; is_active?: boolean }): Promise<AxiosResponse<ReasonCode>> =>
    api.post('/purchasing/reason-codes/', data),
  updateReasonCode: (id: number, data: Partial<{ code: string; description: string; category: string; is_active: boolean }>): Promise<AxiosResponse<ReasonCode>> =>
    api.patch(`/purchasing/reason-codes/${id}/`, data),
  // CR-5886: returns 204 when permanently deleted, or 200 with { archived: true }
  // when the code was in use and archived instead.
  deleteReasonCode: (id: number): Promise<AxiosResponse<{ archived: boolean; detail: string; reason_code: ReasonCode } | void>> =>
    api.delete(`/purchasing/reason-codes/${id}/`),

  getReasonCode: (id: number): Promise<AxiosResponse<ReasonCode>> =>
    api.get(`/purchasing/reason-codes/${id}/`),

  // CR-5886: create/edit/archive audit history for one reason code (admin only).
  // Cache-busting param so the trail is always fresh after a change in-session.
  getReasonCodeAudit: (id: number): Promise<AxiosResponse<ReasonCodeAudit[]>> =>
    api.get(`/purchasing/reason-codes/${id}/audit/`, { params: { _: Date.now() } }),
  // CR-5886: un-archive a code (admin only) — returns it Disabled.
  restoreReasonCode: (id: number): Promise<AxiosResponse<ReasonCode>> =>
    api.post(`/purchasing/reason-codes/${id}/restore/`),

  // PHASE 1 - Goods Receipt Notes (GRNs)
  getGRNs: (params?: QueryParams): Promise<AxiosResponse<{ count: number; results: GoodsReceiptNote[] }>> =>
    api.get('/purchasing/grns/', { params }),

  getGRN: (id: number): Promise<AxiosResponse<GoodsReceiptNote>> =>
    api.get(`/purchasing/grns/${id}/`),

  createGRN: (data: Partial<GoodsReceiptNote>): Promise<AxiosResponse<GoodsReceiptNote>> =>
    api.post('/purchasing/grns/', data),

  updateGRN: (id: number, data: Partial<GoodsReceiptNote>): Promise<AxiosResponse<GoodsReceiptNote>> =>
    api.put(`/purchasing/grns/${id}/`, data),

  // CR-3861: Export GRNs/Deliveries
  exportGRNs: (params?: QueryParams): Promise<AxiosResponse<Blob>> =>
    api.get('/purchasing/grns/export/', { params, responseType: 'blob' }),

  // Superuser-only: GRNs the attribution pipeline left unallocated
  // (no schedule FK + zero allocation rows).
  getUnallocatedGRNs: (params?: QueryParams): Promise<AxiosResponse<{ count: number; results: GoodsReceiptNote[] }>> =>
    api.get('/purchasing/grns/unallocated/', { params }),

  // Superuser-only manual override: link an unallocated GRN to a schedule line.
  linkGRNToSchedule: (id: number, scheduleId: number): Promise<AxiosResponse<{
    detail: string;
    grn_id: number;
    schedule_id: number;
    allocated_qty: string;
    on_time_status: string | null;
    schedule_shipped_qty: string;
    schedule_balance_qty: string;
    schedule_status: string;
  }>> =>
    api.post(`/purchasing/grns/${id}/link-schedule/`, { schedule_id: scheduleId }),

  // GRN Workflow Actions
  acknowledgeGRN: (id: number, data: { comments?: string }): Promise<AxiosResponse<{ status: string; message: string; data: GoodsReceiptNote }>> =>
    api.post(`/purchasing/grns/${id}/acknowledge/`, data),

  // CR-8214: customer bulk (multi-select) acknowledge — what the per-row
  // Acknowledge tick does, for every selected delivery. Ineligible rows are
  // skipped server-side with a reason.
  bulkAcknowledgeGRNs: (
    data: { grn_ids: number[]; comments?: string },
  ): Promise<AxiosResponse<BulkAcknowledgeResult>> =>
    api.post('/purchasing/grns/bulk-acknowledge/', data),

  disputeGRN: (id: number, data: { reason_id: number; comments?: string }): Promise<AxiosResponse<{ status: string; message: string; data: GoodsReceiptNote }>> =>
    api.post(`/purchasing/grns/${id}/dispute/`, data),

  resolveGRNDispute: (id: number, data: { comments?: string }): Promise<AxiosResponse<{ status: string; message: string; data: GoodsReceiptNote }>> =>
    api.post(`/purchasing/grns/${id}/resolve-dispute/`, data),

  // GRN Packing Slip Lookup
  getGRNsByPackingSlip: (packingSlipNumber: string): Promise<AxiosResponse<{
    status: string;
    message: string;
    count: number;
    data: GoodsReceiptNote[];
  }>> =>
    api.get('/purchasing/grns/by-packing-slip/', { params: { number: packingSlipNumber } }),

  // PHASE 1 - Advanced Shipping Notices (ASNs)
  getASNs: (params?: QueryParams): Promise<AxiosResponse<{ count: number; results: AdvancedShippingNotice[] }>> =>
    api.get('/purchasing/asns/', { params }),

  /**
   * CR-6441 / CR-6160: vocabularies for the ASN filter panel. The courier
   * delivery status dropdown is built from this so it can never drift from
   * the values the model accepts.
   */
  getASNFilterOptions: (): Promise<AxiosResponse<{
    courier_delivery_statuses: Array<{ value: string; label: string }>;
    /**
     * CR-6441 re-test: suppliers whose ASNs the caller can see, each with the
     * customer site ids it has shipped to — the Site → Supplier Site cascade.
     * Scoped like the ASN list (accessible companies + view context).
     */
    suppliers?: Array<{ id: number; name: string; group?: number | null; country?: string | null; customers?: number[] }>;
  }>> =>
    api.get('/purchasing/asns/filter-options/'),

  getASN: (id: number): Promise<AxiosResponse<AdvancedShippingNotice>> =>
    api.get(`/purchasing/asns/${id}/`),

  createASN: (data: Record<string, unknown>): Promise<AxiosResponse<AdvancedShippingNotice>> =>
    api.post('/purchasing/asns/', data),

  /**
   * Get available delivery lines not yet linked to an ASN.
   * CR-6038: optional source_type filter — 'CallOff' for the Create ASN
   * wizard's Call-Off Delivery Lines tab, 'SupplierImport' for the
   * standalone Import & Allocate post-upload table. Omit to fetch both.
   */
  getAvailableDeliveryLines: (params?: { source_type?: 'CallOff' | 'SupplierImport' }): Promise<AxiosResponse<Record<string, unknown>[]>> =>
    api.get('/purchasing/asns/available-delivery-lines/', { params }),

  /** CR-4888 (Slice D): download blank ASN delivery-line import template */
  downloadASNTemplate: (): Promise<AxiosResponse<Blob>> =>
    api.get('/purchasing/asns/import-template/', { responseType: 'blob' }),

  /** CR-4888 (Slice D): upload populated ASN delivery-line spreadsheet */
  importASNLines: (formData: FormData): Promise<AxiosResponse<{
    total: number;
    imported: number;
    ignored: number;
    errors: number;
    error_details: Array<{ row: number; errors: string[] }>;
    delivery_line_ids: number[];
  }>> =>
    api.post('/purchasing/asns/import-lines/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),

  /** CR-6038: attach already-imported delivery lines to an existing draft ASN. */
  attachDeliveryLinesToASN: (asnId: number, deliveryLineIds: number[]): Promise<AxiosResponse<{
    attached: number;
    asn_id: number;
  }>> =>
    api.post(`/purchasing/asns/${asnId}/attach-delivery-lines/`, {
      delivery_line_ids: deliveryLineIds,
    }),

  /** CR-6038: split one unallocated delivery line into N children whose qtys sum to the original. */
  splitDeliveryLine: (deliveryLineId: number, splits: number[]): Promise<AxiosResponse<{
    split_ids: number[];
  }>> =>
    api.post('/purchasing/asns/split-delivery-line/', {
      delivery_line_id: deliveryLineId,
      splits,
    }),

  updateASN: (id: number, data: Partial<AdvancedShippingNotice>): Promise<AxiosResponse<AdvancedShippingNotice>> =>
    api.patch(`/purchasing/asns/${id}/`, data),

  // ASN Workflow Actions
  submitASN: (id: number): Promise<AxiosResponse<{ status: string; message: string; data: AdvancedShippingNotice }>> =>
    api.post(`/purchasing/asns/${id}/submit/`),

  // CR-6160: manually re-pull live courier delivery status (paid module).
  refreshASNTracking: (id: number): Promise<AxiosResponse<{ status: string; message: string; data: AdvancedShippingNotice }>> =>
    api.post(`/purchasing/asns/${id}/refresh-tracking/`),

  acknowledgeASN: (id: number): Promise<AxiosResponse<{ status: string; message: string; data: AdvancedShippingNotice }>> =>
    api.post(`/purchasing/asns/${id}/acknowledge/`),

  markASNDelivered: (id: number, data: { actual_delivery_date?: string }): Promise<AxiosResponse<{ status: string; message: string; data: AdvancedShippingNotice }>> =>
    api.post(`/purchasing/asns/${id}/mark-delivered/`, data),

  calculateASNCarbon: (id: number): Promise<AxiosResponse<{ status: string; message: string; carbon_emission_kg: string; data: AdvancedShippingNotice }>> =>
    api.post(`/purchasing/asns/${id}/calculate-carbon/`),

  printASN: (id: number): Promise<AxiosResponse<Blob>> =>
    api.get(`/purchasing/asns/${id}/print/`, { responseType: 'blob' }),

  /** CR-6322: the active customer's ASN delivery-note column layout (merged
   *  with the default preset). Used to seed the admin column picker. */
  getASNColumnFormat: (): Promise<AxiosResponse<ASNColumnFormatResponse>> =>
    api.get('/purchasing/asn-column-format/'),

  /** CR-6322: save the active company's ASN delivery-note column layout.
   *  Company admins only (server gates via IsHelixOrCompanyAdmin), which
   *  includes supplier admins configuring their own grid + CSV (CR-7194).
   *  Pass `export_header` back untouched — the save replaces the whole layout,
   *  so omitting it wipes any fixed ERP header names. */
  saveASNColumnFormat: (
    columns: Array<{
      column_id: string;
      is_visible: boolean;
      sort_order: number;
      export_header?: string;
    }>,
  ): Promise<AxiosResponse<ASNColumnFormatResponse>> =>
    api.put('/purchasing/asn-column-format/save/', columns),

  // Get ASNs for a specific PO line
  getLineASNs: (lineId: number): Promise<AxiosResponse<{
    status: string;
    total_asns: number;
    total_shipped: string;
    asns: AdvancedShippingNotice[];
  }>> =>
    api.get('/purchasing/asns/by-po-line/', { params: { po_line_id: lineId } }),

  // PHASE 1 - Changelog
  getChangelogs: (params?: QueryParams): Promise<AxiosResponse<{ count: number; results: POChangeLog[] }>> =>
    api.get('/purchasing/changelog/', { params }),

  getChangelog: (id: number): Promise<AxiosResponse<POChangeLog>> =>
    api.get(`/purchasing/changelog/${id}/`),

  // CR-4865: PO Change Request attachments
  getChangeRequestAttachments: (params: { po_line?: number; change_request_ref?: number }):
    Promise<AxiosResponse<{ count: number; results: POChangeRequestAttachment[] }>> =>
    api.get('/purchasing/po-change-request-attachments/', { params }),

  uploadChangeRequestAttachment: (data: {
    po_line: number;
    change_request_ref: number;
    side: 'submission' | 'decision';
    file: File;
  }): Promise<AxiosResponse<POChangeRequestAttachment>> => {
    const formData = new FormData();
    formData.append('po_line', String(data.po_line));
    formData.append('change_request_ref', String(data.change_request_ref));
    formData.append('side', data.side);
    formData.append('file', data.file);
    return api.post('/purchasing/po-change-request-attachments/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  deleteChangeRequestAttachment: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/purchasing/po-change-request-attachments/${id}/`),

  downloadChangeRequestAttachment: (id: number): Promise<AxiosResponse<Blob>> =>
    api.get(`/purchasing/po-change-request-attachments/${id}/download/`, { responseType: 'blob' }),

  // CR-4880: proxy download for contract attachments (avoids S3/URL issues on direct link)
  downloadContractFile: (id: number): Promise<AxiosResponse<Blob>> =>
    api.get(`/purchasing/contract-prices/${id}/download-file/`, { responseType: 'blob' }),

  // Data Import
  importData: (formData: FormData): Promise<AxiosResponse<{
    success: boolean;
    message: string;
    data: {
      suppliers_created: number;
      suppliers_updated: number;
      pos_created: number;
      lines_created: number;
      grns_created: number;
      grns_skipped: number;
      errors: string[];
    };
  }>> => {
    return api.post('/purchasing/import-data/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 600000, // 10 min for large imports
    });
  },

  getImportTemplates: (): Promise<AxiosResponse<{
    templates: {
      suppliers: {
        description: string;
        required_columns: string[];
        sheet_name: string;
      };
      purchase_orders: {
        description: string;
        required_columns: string[];
      };
      grns: {
        description: string;
        required_columns: string[];
      };
    };
  }>> =>
    api.get('/purchasing/import-templates/'),

  downloadImportTemplates: (type?: string): Promise<AxiosResponse<Blob>> =>
    api.get('/purchasing/download-templates/', { responseType: 'blob', params: type ? { type } : {} }),

  // Paragon ERP Integration - WriteBack API
  getWriteBacks: (params?: QueryParams): Promise<AxiosResponse<{ count: number; results: WriteBack[] }>> =>
    api.get('/purchasing/writebacks/', { params }),

  getWriteBack: (id: number): Promise<AxiosResponse<WriteBack>> =>
    api.get(`/purchasing/writebacks/${id}/`),

  markWriteBackSynced: (id: number): Promise<AxiosResponse<{ status: string; synced_at: string; message: string }>> =>
    api.post(`/purchasing/writebacks/${id}/mark_synced/`),

  bulkMarkWriteBacksSynced: (ids: number[]): Promise<AxiosResponse<{ status: string; synced_count: number; message: string }>> =>
    api.post('/purchasing/writebacks/bulk_mark_synced/', { ids }),

  exportWriteBacks: (params?: QueryParams): Promise<AxiosResponse<Blob>> =>
    api.get('/purchasing/writebacks/export/', { params, responseType: 'blob' }),

  getWriteBackStats: (): Promise<AxiosResponse<WriteBackStats>> =>
    api.get('/purchasing/writebacks/stats/'),

  // Contract Pricing
  getContractPrices: (params?: QueryParams): Promise<AxiosResponse<{ count: number; results: ContractPrice[] }>> =>
    api.get('/purchasing/contract-prices/', { params }),

  getContractPrice: (id: number): Promise<AxiosResponse<ContractPrice>> =>
    api.get(`/purchasing/contract-prices/${id}/`),

  createContractPrice: (data: Partial<ContractPrice>): Promise<AxiosResponse<ContractPrice>> =>
    api.post('/purchasing/contract-prices/', data),

  // CR-5770: master-contract create flow needs to upload the contract file at
  // creation time, not as a follow-up edit, so we expose a multipart variant.
  createContractPriceFormData: (data: FormData): Promise<AxiosResponse<ContractPrice>> =>
    api.post('/purchasing/contract-prices/', data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),

  // CR-7787: Customer and Contract Owner choices for the supplier contract
  // forms. Pass a customer to get its owners.
  getContractFormOptions: (
    params?: { customer?: number; supplier?: number },
  ): Promise<AxiosResponse<ContractFormOptions>> =>
    api.get('/purchasing/contract-prices/form-options/', { params }),

  updateContractPrice: (id: number, data: Partial<ContractPrice>): Promise<AxiosResponse<ContractPrice>> =>
    api.patch(`/purchasing/contract-prices/${id}/`, data),

  updateContractPriceFormData: (id: number, data: FormData): Promise<AxiosResponse<ContractPrice>> =>
    api.patch(`/purchasing/contract-prices/${id}/`, data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),

  deleteContractPrice: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/purchasing/contract-prices/${id}/`),

  // Customer review 11 Sep (item 21): + derived_created (the synthetic first entry).
  getContractPriceChangelog: (id: number, params?: QueryParams): Promise<AxiosResponse<ContractPriceChangelogResponse>> =>
    api.get(`/purchasing/contract-prices/${id}/changelog/`, { params }),

  exportContractPrices: (params?: QueryParams): Promise<AxiosResponse<Blob>> =>
    api.get('/purchasing/contract-prices/export/', { params, responseType: 'blob' }),

  /** CR-7383: download the blank Price Book import template */
  downloadContractPriceImportTemplate: (): Promise<AxiosResponse<Blob>> =>
    api.get('/purchasing/contract-prices/import-template/', { responseType: 'blob' }),

  importContractPricesPreview: (formData: FormData): Promise<AxiosResponse<{
    status: string;
    message: string;
    total_rows: number;
    error_count: number;
    errors: Array<{ row: number; errors: string[] }>;
    preview: ContractPriceImportPreview[];
    column_mapping: Record<string, string>;
  }>> =>
    // CR-6228: large files (hundreds of rows) validate every row against the
    // DB, which can exceed the default 120s client timeout. Allow up to 10 min.
    api.post('/purchasing/contract-prices/import-preview/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 600000,
    }),

  importContractPricesConfirm: (data: { rows: ContractPriceImportPreview[] }): Promise<AxiosResponse<{
    status: string;
    message: string;
    created_contracts: number;
    created_parts: number;
    updated_parts: number;
    errors: Array<{ contract_number: string; error: string }>;
  }>> =>
    // CR-6228: confirming a large import creates many parts and recalculates
    // PPV server-side, which can run well past the default 120s timeout. Allow
    // up to 10 min so the success response is received instead of the client
    // aborting (which left the list un-refreshed and showed no success state).
    api.post('/purchasing/contract-prices/import-confirm/', data, {
      timeout: 600000,
    }),

  // Contract Pricing Parts
  getContractPriceParts: (params?: QueryParams): Promise<AxiosResponse<{ count: number; results: ContractPricePart[] }>> =>
    api.get('/purchasing/contract-price-parts/', { params }),

  getContractPricePart: (id: number): Promise<AxiosResponse<ContractPricePart>> =>
    api.get(`/purchasing/contract-price-parts/${id}/`),

  createContractPricePart: (data: Partial<ContractPricePart>): Promise<AxiosResponse<ContractPricePart>> =>
    api.post('/purchasing/contract-price-parts/', data),

  updateContractPricePart: (id: number, data: Partial<ContractPricePart>): Promise<AxiosResponse<ContractPricePart>> =>
    api.put(`/purchasing/contract-price-parts/${id}/`, data),

  deleteContractPricePart: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/purchasing/contract-price-parts/${id}/`),

  // CR-3381: PO Document Management
  getPODocuments: (params?: QueryParams): Promise<AxiosResponse<{ count: number; results: PODocument[] }>> =>
    api.get('/purchasing/documents/', { params }),

  getPODocument: (id: number): Promise<AxiosResponse<PODocument>> =>
    api.get(`/purchasing/documents/${id}/`),

  uploadPODocument: (data: FormData): Promise<AxiosResponse<PODocument>> =>
    api.post('/purchasing/documents/', data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),

  updatePODocument: (id: number, data: FormData): Promise<AxiosResponse<PODocument>> =>
    api.patch(`/purchasing/documents/${id}/`, data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),

  deletePODocument: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/purchasing/documents/${id}/`),

  downloadPODocument: (id: number): Promise<AxiosResponse<Blob>> =>
    api.get(`/purchasing/documents/${id}/download/`, { responseType: 'blob' }),

  getPODocumentsByPO: (poId: number): Promise<AxiosResponse<{
    documents: PODocument[];
    grouped: Record<string, Record<string, PODocument[]>>;
    total_count: number;
  }>> =>
    api.get('/purchasing/documents/by_po/', { params: { po_id: poId } }),

  getPODocumentTypes: (): Promise<AxiosResponse<{
    customer_types: Array<{ value: string; label: string }>;
    supplier_types: Array<{ value: string; label: string }>;
  }>> =>
    api.get('/purchasing/documents/document_types/'),

  // CR-3381: GRN Document Management
  getGRNDocuments: (params?: QueryParams): Promise<AxiosResponse<{ count: number; results: GRNDocument[] }>> =>
    api.get('/purchasing/grn-documents/', { params }),

  getGRNDocument: (id: number): Promise<AxiosResponse<GRNDocument>> =>
    api.get(`/purchasing/grn-documents/${id}/`),

  uploadGRNDocument: (data: FormData): Promise<AxiosResponse<GRNDocument>> =>
    api.post('/purchasing/grn-documents/', data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),

  updateGRNDocument: (id: number, data: FormData): Promise<AxiosResponse<GRNDocument>> =>
    api.patch(`/purchasing/grn-documents/${id}/`, data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),

  deleteGRNDocument: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/purchasing/grn-documents/${id}/`),

  downloadGRNDocument: (id: number): Promise<AxiosResponse<Blob>> =>
    api.get(`/purchasing/grn-documents/${id}/download/`, { responseType: 'blob' }),

  getGRNDocumentsByGRN: (grnId: number): Promise<AxiosResponse<{
    documents: GRNDocument[];
    grouped: Record<string, GRNDocument[]>;
    total_count: number;
  }>> =>
    api.get('/purchasing/grn-documents/by_grn/', { params: { grn_id: grnId } }),

  getGRNDocumentTypes: (): Promise<AxiosResponse<Array<{ value: string; label: string }>>> =>
    api.get('/purchasing/grn-documents/document_types/'),

  // CR-3381: ASN Document Management
  getASNDocuments: (params?: QueryParams): Promise<AxiosResponse<{ count: number; results: ASNDocument[] }>> =>
    api.get('/purchasing/asn-documents/', { params }),

  getASNDocument: (id: number): Promise<AxiosResponse<ASNDocument>> =>
    api.get(`/purchasing/asn-documents/${id}/`),

  uploadASNDocument: (data: FormData): Promise<AxiosResponse<ASNDocument>> =>
    api.post('/purchasing/asn-documents/', data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),

  updateASNDocument: (id: number, data: FormData): Promise<AxiosResponse<ASNDocument>> =>
    api.patch(`/purchasing/asn-documents/${id}/`, data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),

  deleteASNDocument: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/purchasing/asn-documents/${id}/`),

  downloadASNDocument: (id: number): Promise<AxiosResponse<Blob>> =>
    api.get(`/purchasing/asn-documents/${id}/download/`, { responseType: 'blob' }),

  getASNDocumentsByASN: (asnId: number): Promise<AxiosResponse<{
    documents: ASNDocument[];
    grouped: Record<string, ASNDocument[]>;
    total_count: number;
  }>> =>
    api.get('/purchasing/asn-documents/by_asn/', { params: { asn_id: asnId } }),

  getASNDocumentTypes: (): Promise<AxiosResponse<Array<{ value: string; label: string }>>> =>
    api.get('/purchasing/asn-documents/document_types/'),

  // CR-3381: Document Download Tracking
  getDocumentDownloads: (params?: QueryParams): Promise<AxiosResponse<{ count: number; results: PurchasingDocumentDownload[] }>> =>
    api.get('/purchasing/document-downloads/', { params }),

  getDocumentDownloadsByDocument: (entityType: 'po' | 'grn' | 'asn', documentId: number): Promise<AxiosResponse<{
    downloads: PurchasingDocumentDownload[];
    total_count: number;
  }>> =>
    api.get('/purchasing/document-downloads/by_document/', { params: { entity_type: entityType, document_id: documentId } }),

  // CR-3388: Supplier Query System
  getSupplierQueries: (params?: QueryParams): Promise<AxiosResponse<{ count: number; results: SupplierQuery[] }>> =>
    api.get('/purchasing/supplier-queries/', { params }),

  getSupplierQuery: (id: number): Promise<AxiosResponse<SupplierQuery>> =>
    api.get(`/purchasing/supplier-queries/${id}/`),

  createSupplierQuery: (data: {
    po_line: number;
    query_type: SupplierQueryType;
    query_text: string;
    reference_value?: string;
    priority?: SupplierQueryPriority;
    reason_code?: number;
  }): Promise<AxiosResponse<SupplierQuery>> =>
    api.post('/purchasing/supplier-queries/', data),

  updateSupplierQuery: (id: number, data: Partial<SupplierQuery>): Promise<AxiosResponse<SupplierQuery>> =>
    api.patch(`/purchasing/supplier-queries/${id}/`, data),

  deleteSupplierQuery: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/purchasing/supplier-queries/${id}/`),

  acknowledgeSupplierQuery: (id: number): Promise<AxiosResponse<{ status: string; message: string; data: SupplierQuery }>> =>
    api.post(`/purchasing/supplier-queries/${id}/acknowledge/`),

  respondToSupplierQuery: (id: number, data: { response_text: string; mark_resolved?: boolean }): Promise<AxiosResponse<{ status: string; message: string; data: SupplierQuery }>> =>
    api.post(`/purchasing/supplier-queries/${id}/respond/`, data),

  resolveSupplierQuery: (id: number, data: { resolution_notes?: string }): Promise<AxiosResponse<{ status: string; message: string; data: SupplierQuery }>> =>
    api.post(`/purchasing/supplier-queries/${id}/resolve/`, data),

  getSupplierQueriesByPOLine: (poLineId: number): Promise<AxiosResponse<{ status: string; count: number; data: SupplierQuery[] }>> =>
    api.get('/purchasing/supplier-queries/by-po-line/', { params: { po_line_id: poLineId } }),

  getSupplierQueryStats: (): Promise<AxiosResponse<SupplierQueryStats>> =>
    api.get('/purchasing/supplier-queries/stats/'),

  // CR-3414: Forecast Tab
  getForecastTableData: (params?: {
    company_type?: 'customer' | 'supplier';
    part_number?: string;
    commodity_group?: string;
    supplier_id?: number;
    customer_id?: number;
    page?: number;
    page_size?: number;
  }): Promise<AxiosResponse<ForecastTableResponse>> =>
    api.get('/purchasing/forecasts/table-data/', { params }),

  refreshForecastSnapshot: (params?: { customer_id?: number }): Promise<AxiosResponse<{
    queued?: boolean;
    skipped?: boolean;
    reason?: string;
    customer_id?: number;
  }>> =>
    api.post('/purchasing/forecasts/refresh/', params || {}),

  getForecastRefreshStatus: (params?: { customer_id?: number }): Promise<AxiosResponse<{
    enabled: boolean;
    customer_id?: number;
    last_refreshed_at: string | null;
    last_status?: 'ok' | 'running' | 'failed' | 'never_run';
    last_error?: string;
    last_duration_seconds?: number;
    last_row_count?: number;
  }>> =>
    api.get('/purchasing/forecasts/refresh-status/', { params }),

  getForecastCellDetail: (params: {
    customer_id?: number;
    supplier_id?: number;
    part_number: string;
    record_type: 1 | 2;
    date_bucket: string;
    page?: number;
    page_size?: number;
  }): Promise<AxiosResponse<{ results: ForecastCellDetail[]; total_count: number }>> =>
    api.get('/purchasing/forecasts/cell-detail/', { params }),

  getForecastFilterOptions: (): Promise<AxiosResponse<ForecastFilterOptions>> =>
    api.get('/purchasing/forecasts/filter-options/'),

  importForecasts: (data: FormData): Promise<AxiosResponse<{ status: string; imported: number; errors: string[] }>> =>
    api.post('/purchasing/forecasts/import/', data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),

  // Granular file-based imports (all share the same response shape)
  // Use 10-minute timeout for large file imports
  importCommodityGroups: (formData: FormData): Promise<AxiosResponse<{
    success: boolean; message: string; data: { created: number; updated: number; skipped: number; errors: string[] };
  }>> =>
    api.post('/purchasing/import-commodity-groups/', formData, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 600000 }),

  importFaultCodes: (formData: FormData): Promise<AxiosResponse<{
    success: boolean; message: string; data: { created: number; updated: number; skipped: number; errors: string[] };
  }>> =>
    api.post('/purchasing/import-fault-codes/', formData, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 600000 }),

  importDepartments: (formData: FormData): Promise<AxiosResponse<{
    success: boolean; message: string; data: { created: number; updated: number; skipped: number; errors: string[] };
  }>> =>
    api.post('/purchasing/import-departments/', formData, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 600000 }),

  importSupplierAdditional: (formData: FormData): Promise<AxiosResponse<{
    success: boolean; message: string; data: { created: number; updated: number; skipped: number; errors: string[] };
  }>> =>
    api.post('/purchasing/import-supplier-additional/', formData, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 600000 }),

  importNCRs: (formData: FormData): Promise<AxiosResponse<{
    success: boolean; message: string; data: { created: number; updated: number; skipped: number; errors: string[] };
  }>> =>
    api.post('/purchasing/import-ncrs/', formData, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 600000 }),

  importForecastsFile: (formData: FormData): Promise<AxiosResponse<{
    success: boolean; message: string; data: { created: number; updated: number; skipped: number; errors: string[] };
  }>> =>
    api.post('/purchasing/import-forecasts-file/', formData, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 600000 }),

  importUsers: (formData: FormData): Promise<AxiosResponse<{
    success: boolean; message: string; data: { created: number; updated: number; skipped: number; errors: string[] };
  }>> =>
    api.post('/purchasing/import-users/', formData, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 600000 }),

  // CR-3413: Commodity Management CRUD
  getCommodityGroups: (params?: QueryParams): Promise<AxiosResponse<CommodityGroup[]>> =>
    api.get('/purchasing/commodity-groups/', { params }),

  createCommodityGroup: (data: { name: string; enabled?: boolean }): Promise<AxiosResponse<CommodityGroup>> =>
    api.post('/purchasing/commodity-groups/', data),

  updateCommodityGroup: (id: number, data: { name?: string; enabled?: boolean }): Promise<AxiosResponse<CommodityGroup>> =>
    api.patch(`/purchasing/commodity-groups/${id}/`, data),

  deleteCommodityGroup: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/purchasing/commodity-groups/${id}/`),

  getCommodityTypes: (params?: QueryParams): Promise<AxiosResponse<CommodityType[]>> =>
    api.get('/purchasing/commodity-types/', { params }),

  createCommodityType: (data: { name: string; commodity_group: number; enabled?: boolean }): Promise<AxiosResponse<CommodityType>> =>
    api.post('/purchasing/commodity-types/', data),

  updateCommodityType: (id: number, data: { name?: string; commodity_group?: number; enabled?: boolean }): Promise<AxiosResponse<CommodityType>> =>
    api.patch(`/purchasing/commodity-types/${id}/`, data),

  deleteCommodityType: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/purchasing/commodity-types/${id}/`),

  getCommoditySupplierMap: (): Promise<AxiosResponse<Record<string, { groups: number[]; types: number[] }>>> =>
    api.get('/purchasing/commodity-groups/supplier-map/'),

  // CR-1441: Contract Type CRUD (per-company configurable contract types).
  getContractTypes: (params?: QueryParams): Promise<AxiosResponse<{ count: number; results: ContractType[] }>> =>
    api.get('/purchasing/contract-types/', { params }),

  createContractType: (data: { name: string; abbreviation: string; enabled?: boolean }): Promise<AxiosResponse<ContractType>> =>
    api.post('/purchasing/contract-types/', data),

  updateContractType: (id: number, data: { name?: string; abbreviation?: string; enabled?: boolean }): Promise<AxiosResponse<ContractType>> =>
    api.patch(`/purchasing/contract-types/${id}/`, data),

  deleteContractType: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/purchasing/contract-types/${id}/`),

  exportForecasts: (params?: {
    /** Output format — 'csv' (default) or 'xlsx'. */
    file_format?: 'csv' | 'xlsx';
    part_number?: string;
    /** Contributing OPEN PO number (contains-match). */
    po_search?: string;
    /** Quick search — matches part number or the supplier's own reference. */
    part_search?: string;
    supplier_part_number?: string;
    commodity_group?: string;
    commodity_type?: string;
    supplier_group?: string;
    supplier_id?: number;
    customer_id?: number;
    record_type_filter?: number;
    import_date_from?: string;
    import_date_to?: string;
    /** CR-6448: exact import date and warehouse code from the supplier panel. */
    import_date?: string;
    warehouse_code?: string;
    /** CR-6440 rework: the customer panel's Supplier Code (exact match). */
    supplier_code?: string;
    /** CR-6440: Company / Supplier section filters of the customer panel. */
    company_country?: string;
    division?: number;
    supplier_country?: string;
    view_level?: string;
    group?: number;
    company?: number;
    site?: number;
  }): Promise<AxiosResponse<Blob>> =>
    api.get('/purchasing/forecasts/export/', { params, responseType: 'blob' }),

  // Forecast CRUD
  getForecasts: (params?: QueryParams): Promise<AxiosResponse<{ count: number; results: POForecast[] }>> =>
    api.get('/purchasing/forecasts/', { params }),

  getForecast: (id: number): Promise<AxiosResponse<POForecast>> =>
    api.get(`/purchasing/forecasts/${id}/`),

  createForecast: (data: Partial<POForecast>): Promise<AxiosResponse<POForecast>> =>
    api.post('/purchasing/forecasts/', data),

  updateForecast: (id: number, data: Partial<POForecast>): Promise<AxiosResponse<POForecast>> =>
    api.patch(`/purchasing/forecasts/${id}/`, data),

  deleteForecast: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/purchasing/forecasts/${id}/`),

  // Part Schedules (Firm Boundary Configuration)
  getPartSchedules: (params?: QueryParams): Promise<AxiosResponse<{ count: number; results: PartSchedule[] }>> =>
    api.get('/purchasing/part-schedules/', { params }),

  getPartSchedule: (id: number): Promise<AxiosResponse<PartSchedule>> =>
    api.get(`/purchasing/part-schedules/${id}/`),

  createPartSchedule: (data: Partial<PartSchedule>): Promise<AxiosResponse<PartSchedule>> =>
    api.post('/purchasing/part-schedules/', data),

  updatePartSchedule: (id: number, data: Partial<PartSchedule>): Promise<AxiosResponse<PartSchedule>> =>
    api.patch(`/purchasing/part-schedules/${id}/`, data),

  deletePartSchedule: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/purchasing/part-schedules/${id}/`),

  // Part Schedule Entries (isolated part-level module, per-customer opt-in)
  getPartScheduleEntries: (
    params?: QueryParams,
  ): Promise<AxiosResponse<{ count: number; results: PartScheduleEntry[] }>> =>
    api.get('/purchasing/part-schedule-entries/', { params }),

  getPartScheduleEntry: (id: number): Promise<AxiosResponse<PartScheduleEntry>> =>
    api.get(`/purchasing/part-schedule-entries/${id}/`),

  /** Distinct supplier names for the Part Schedule Supplier quick-filter. */
  getPartScheduleSupplierOptions: (): Promise<AxiosResponse<{ suppliers: string[] }>> =>
    api.get('/purchasing/part-schedule-entries/supplier-options/'),

  /** Parts on open scheduling-agreement lines with no ERP firm window. */
  getPartsNotFromErp: (
    params?: QueryParams,
  ): Promise<AxiosResponse<{ count: number; results: PartNotFromErp[] }>> =>
    api.get('/purchasing/part-schedule-entries/not-from-erp/', { params }),

  /** Supplier names for the "Not from ERP" tab's Supplier filter. */
  getPartsNotFromErpSupplierOptions: (): Promise<AxiosResponse<{ suppliers: string[] }>> =>
    api.get('/purchasing/part-schedule-entries/not-from-erp/supplier-options/'),

  /** Set (days) or clear (null) the manual firm window for such a part — customer admins. */
  setPartManualFirm: (
    data: { part_number: string; manual_schedule_firm: number | null; customer_id?: number },
  ): Promise<AxiosResponse<PartNotFromErp>> =>
    api.post('/purchasing/part-schedule-entries/set-manual-firm/', data),

  /**
   * Export Part Schedule entries to Excel.
   *
   * Pass export_all to ignore the on-screen filters and export the whole
   * table; omit it to export exactly what the filters match.
   */
  exportPartScheduleEntries: (
    params?: QueryParams & { export_all?: boolean },
  ): Promise<AxiosResponse<Blob>> =>
    api.get('/purchasing/part-schedule-entries/export/', {
      params,
      responseType: 'blob',
    }),

  // Forecast History (Snapshots for Variance Analysis)
  getForecastHistory: (params?: {
    part_number?: string;
    snapshot_date?: string;
    page?: number;
    page_size?: number;
  }): Promise<AxiosResponse<{ count: number; results: ForecastHistory[] }>> =>
    api.get('/purchasing/forecast-history/', { params }),

  // CR-3409: PO Line Communication Log
  getLineMessages: (params: { po_line: number; page?: number; page_size?: number }): Promise<AxiosResponse<{ count: number; results: POLineMessage[] }>> =>
    api.get('/purchasing/messages/', { params: { ...params, ordering: 'sent_date' } }),

  // CR-6409: the other side's messages on a line that nobody on this side has read yet.
  getLineUnreadMessageCount: (lineId: number): Promise<AxiosResponse<{ unread_count: number }>> =>
    api.get('/purchasing/messages/unread-count/', { params: { po_line: lineId } }),

  createLineMessage: (data: { po: number; po_line: number; message_text: string }): Promise<AxiosResponse<POLineMessage>> =>
    api.post('/purchasing/messages/', data),

  markMessageRead: (id: number): Promise<AxiosResponse<POLineMessage>> =>
    api.post(`/purchasing/messages/${id}/mark_read/`),

  markMessageComplete: (id: number): Promise<AxiosResponse<POLineMessage>> =>
    api.post(`/purchasing/messages/${id}/mark_complete/`),

  markAllMessagesRead: (poLineId: number): Promise<AxiosResponse<{ marked_read: number }>> =>
    api.post('/purchasing/messages/mark-all-read/', { po_line: poLineId }),

  // CR-2220: Strikethrough and soft delete
  deleteLineMessage: (id: number): Promise<AxiosResponse<POLineMessage>> =>
    api.post(`/purchasing/messages/${id}/delete_message/`),

  strikethroughLineMessage: (id: number): Promise<AxiosResponse<POLineMessage>> =>
    api.post(`/purchasing/messages/${id}/strikethrough/`),

  removeStrikethroughLineMessage: (id: number): Promise<AxiosResponse<POLineMessage>> =>
    api.post(`/purchasing/messages/${id}/remove_strikethrough/`),

  // ASN Communication
  getASNMessages: (params: { asn: number; page?: number; page_size?: number }): Promise<AxiosResponse<{ count: number; results: ASNMessage[] }>> =>
    api.get('/purchasing/asn-messages/', { params: { ...params, ordering: 'sent_date' } }),

  createASNMessage: (data: { asn: number; message_text: string }): Promise<AxiosResponse<ASNMessage>> =>
    api.post('/purchasing/asn-messages/', data),

  // Delivery Appeals
  getDeliveryAppeals: (params?: QueryParams) =>
    api.get('/purchasing/delivery-appeals/', { params }),
  getDeliveryAppeal: (id: number) =>
    api.get(`/purchasing/delivery-appeals/${id}/`),
  createDeliveryAppeal: (data: { grn: number; qty_claimed?: string; date_claimed?: string; odd_claimed?: string; supplier_note: string }) =>
    api.post('/purchasing/delivery-appeals/', data),
  respondToDeliveryAppeal: (id: number, data: { action: 'accept' | 'query' | 'reject'; response_note?: string }) =>
    api.post(`/purchasing/delivery-appeals/${id}/respond/`, data),
  updateDeliveryAppeal: (id: number, data: { qty_claimed?: string; date_claimed?: string; odd_claimed?: string; supplier_note?: string }) =>
    api.patch(`/purchasing/delivery-appeals/${id}/`, data),
  cancelDeliveryAppeal: (id: number) =>
    api.post(`/purchasing/delivery-appeals/${id}/cancel/`),
  getDeliveryAppealLogs: (id: number) =>
    api.get(`/purchasing/delivery-appeals/${id}/logs/`),
  getAppealAttachments: (appealId: number) =>
    api.get(`/purchasing/delivery-appeals/${appealId}/attachments/`),
  uploadAppealAttachment: (appealId: number, formData: FormData) =>
    api.post(`/purchasing/delivery-appeals/${appealId}/attachments/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  downloadAppealAttachment: (appealId: number, attachmentId: number): Promise<AxiosResponse<Blob>> =>
    api.get(`/purchasing/delivery-appeals/${appealId}/attachments/${attachmentId}/download/`, { responseType: 'blob' }),
  deleteAppealAttachment: (appealId: number, attachmentId: number) =>
    api.delete(`/purchasing/delivery-appeals/${appealId}/attachments/${attachmentId}/`),
  exportDeliveryAppeals: (params?: QueryParams): Promise<AxiosResponse<Blob>> =>
    api.get('/purchasing/delivery-appeals/export/', { params, responseType: 'blob' }),
  // Supplier bulk (multi-select) "delivered on time" appeal — one appeal per
  // GRN, each claiming its own ODD. Shared reason/note/attachments via multipart.
  bulkCreateDeliveryAppeals: (
    formData: FormData,
  ): Promise<AxiosResponse<BulkAppealResult>> =>
    api.post('/purchasing/delivery-appeals/bulk/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),

  // Customer bulk (multi-select) response — accept OR reject many supplier
  // appeals in one call. Never creates appeals (customer-appeal creation is
  // retired); only supplier-raised appeals in awaiting_response are actioned.
  bulkRespondDeliveryAppeals: (
    data: { appeal_ids: number[]; action: 'accept' | 'reject'; response_note?: string },
  ): Promise<AxiosResponse<BulkRespondResult>> =>
    api.post('/purchasing/delivery-appeals/bulk-respond/', data),

  // Import supplier promise dates from CSV/Excel
  // CR-7066: row_errors names the rejected rows (bad price/qty, missing comment).
  importPromiseDates: (formData: FormData): Promise<AxiosResponse<{
    total: number;
    updated: number;
    ignored: number;
    errors: number;
    row_errors?: Array<{ row: number; po_number: string; line: number | null; message: string }>;
    row_errors_truncated?: number;
  }>> =>
    api.post('/purchasing/lines/import_promise_dates/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),

  // CR-6679: Order Acknowledgement Import
  /** Download pre-filled acknowledgement template with supplier's or customer's PO lines. */
  ackImportTemplate: (params?: QueryParams): Promise<AxiosResponse<Blob>> =>
    api.get('/purchasing/lines/ack-import-template/', { params, responseType: 'blob' }),

  /** Upload file and return validation preview with discrepancies. */
  ackImportPreview: (formData: FormData): Promise<AxiosResponse<{
    status: 'success' | 'error';
    batch_id?: string;
    total_rows: number;
    valid_count: number;
    discrepancy_count: number;
    error_count: number;
    preview: Array<{
      row: number;
      po_number: string;
      line_number: number;
      part_number: string;
      status: 'valid' | 'discrepancy' | 'error';
      discrepancies: Array<{ field: string; po_value: string; supplier_value: string }>;
      errors: string[];
      ack_qty: string | null;
      ack_unit_price: string | null;
      ack_date: string | null;
    }>;
    errors: Array<{ row: number; po_number?: string; line?: number; message: string }>;
    errors_truncated: number;
  }>> =>
    api.post('/purchasing/lines/ack-import-preview/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),

  /** Apply acknowledgements for confirmed rows. */
  ackImportConfirm: (data: {
    batch_id?: string;
    rows: Array<{
      row_number: number;
      po_number: string;
      line_number: number;
      ack_date?: string;
      ack_qty?: number;
      ack_unit_price?: number;
      comment?: string;
    }>;
  }): Promise<AxiosResponse<{
    status: 'success' | 'partial';
    batch_id?: string;
    total: number;
    acknowledged: number;
    /** CR-7328: rows that raised a change request instead of acknowledging */
    change_proposed?: number;
    errors: number;
    error_details: Array<{ row: number; po_number?: string; line?: number; message: string }>;
    errors_truncated: number;
  }>> =>
    api.post('/purchasing/lines/ack-import-confirm/', data),

  /** Download preview results as Excel file. */
  ackImportDownloadPreview: (preview: Array<{
    row: number;
    po_number: string;
    line_number: number;
    part_number: string;
    status: string;
    discrepancies: Array<{ field: string; po_value: string; supplier_value: string }>;
    errors: string[];
    ack_qty: string | null;
    ack_unit_price: string | null;
    ack_date: string | null;
  }>): Promise<AxiosResponse<Blob>> =>
    api.post('/purchasing/lines/ack-import-download-preview/', { preview }, { responseType: 'blob' }),

  /** Cancel a preview upload (mark as cancelled instead of confirmed). */
  ackImportCancel: (batchId: string): Promise<AxiosResponse<{ status: string; batch_id: string }>> =>
    api.post('/purchasing/lines/ack-import-cancel/', { batch_id: batchId }),

  // CR-6679: Acknowledgement Upload History
  /** Get acknowledgement uploads for the current company. */
  getAckUploads: (params?: QueryParams): Promise<AxiosResponse<{ count: number; results: AckUpload[] }>> =>
    api.get('/purchasing/ack-uploads/', { params }),

  /** Get acknowledgement uploads linked to a specific PO. */
  getAckUploadsForPO: (poId: number): Promise<AxiosResponse<AckUpload[]>> =>
    api.get('/purchasing/ack-uploads/for-po/', { params: { po_id: poId } }),

  // CR-5880: milestone CRUD moved to ./milestones (milestonesAPI).
};
